import { useEffect, useState } from "react";

export function useAsyncResource<T>(
  load: () => Promise<T>,
  dependencies: readonly unknown[],
  initialValue: T,
) {
  const [data, setData] = useState(initialValue);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    void load()
      .then((nextData) => {
        if (active) setData(nextData);
      })
      .catch((loadError: unknown) => {
        if (!active) return;
        setError(
          loadError instanceof Error
            ? loadError
            : new Error("Unable to load data."),
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
    // The caller provides the complete reload dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies, revision]);

  const refresh = async () => {
    const nextData = await load();
    setData(nextData);
    setError(null);
    return nextData;
  };

  return {
    data,
    error,
    loading,
    refresh,
    reload: () => setRevision((value) => value + 1),
  };
}
